<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { SNIPPET_CATEGORIES } from '../library/snippets.js';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const alert = useAlert;

const STEP_KEYS = ['import', 'columns', 'validate', 'message', 'schedule'];

const stepLabel = (i, key) => {
  const name = t(`BULK_CAMPAIGNS.IMPORT.STEP_${key.toUpperCase()}`);
  return `${i + 1}. ${name}`;
};

const currentStep = ref(0);
const rawText = ref('');
const fileName = ref('');
const parseResult = ref(null);
const roleOverrides = ref({});
const isParsing = ref(false);
const waStatus = ref({});
const isCheckingWa = ref(false);
const campaignName = ref('');
const inboxId = ref('');
const messageContent = ref('');
const previewIndex = ref(0);
const scheduledAt = ref('');
const sendNowMode = ref(false);
const isCreating = ref(false);
const showLibrary = ref(false);
const libraryCategory = ref('saudacao');

const libraryOptions = computed(() => {
  const cat = SNIPPET_CATEGORIES.find(c => c.key === libraryCategory.value);
  return cat ? cat.options : [];
});

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const contacts = computed(() => parseResult.value?.contacts || []);
const columns = computed(() => parseResult.value?.columns || []);
const flags = computed(() => parseResult.value?.flags || []);
const stats = computed(() => parseResult.value?.stats || {});
const varKeys = computed(() => {
  const keys = new Set();
  contacts.value.forEach(c => {
    Object.keys(c.vars || {}).forEach(k => keys.add(k));
  });
  return [...keys];
});
const previewContact = computed(
  () => contacts.value[previewIndex.value] || null
);
const waCounts = computed(() => {
  let ok = 0;
  let missing = 0;
  let unchecked = 0;
  contacts.value.forEach(c => {
    const st = waStatus.value[c.phone];
    if (st === true) ok += 1;
    else if (st === false) missing += 1;
    else unchecked += 1;
  });
  return { ok, missing, unchecked };
});
const estimatedMinutes = computed(() => {
  const n = contacts.value.length;
  if (!n) return 0;
  const batches = Math.ceil(n / 6);
  return Math.round((n * 67 + (batches - 1) * 3 * 60) / 60);
});
const canNext = computed(() => {
  if (currentStep.value === 0) return !!parseResult.value;
  if (currentStep.value === 1) return contacts.value.length > 0;
  if (currentStep.value === 2) return contacts.value.length > 0;
  if (currentStep.value === 3) return messageContent.value.trim() !== '';
  return false;
});

const roleLabel = role => {
  const map = {
    ignore: t('BULK_CAMPAIGNS.IMPORT.ROLE_IGNORE'),
    name: t('BULK_CAMPAIGNS.IMPORT.ROLE_NAME'),
    phone: t('BULK_CAMPAIGNS.IMPORT.ROLE_PHONE'),
    data: t('BULK_CAMPAIGNS.IMPORT.ROLE_DATA'),
    hora: t('BULK_CAMPAIGNS.IMPORT.ROLE_HORA'),
    exames: t('BULK_CAMPAIGNS.IMPORT.ROLE_EXAMES'),
    local: t('BULK_CAMPAIGNS.IMPORT.ROLE_LOCAL'),
  };
  if (map[role]) return map[role];
  return t('BULK_CAMPAIGNS.IMPORT.ROLE_VAR', {
    key: `{{var.${role}}}`,
  });
};

const varLabel = v => {
  if (v === 'contact.name') return '{{contact.name}}';
  return `{{var.${v}}}`;
};

const varName = v => {
  const map = {
    'contact.name': t('BULK_CAMPAIGNS.IMPORT.VAR_NAME'),
    telefone: t('BULK_CAMPAIGNS.IMPORT.VAR_TELEFONE'),
    data: t('BULK_CAMPAIGNS.IMPORT.VAR_DATA'),
    hora: t('BULK_CAMPAIGNS.IMPORT.VAR_HORA'),
    exames: t('BULK_CAMPAIGNS.IMPORT.VAR_EXAMES'),
    local: t('BULK_CAMPAIGNS.IMPORT.VAR_LOCAL'),
    tem_exames: t('BULK_CAMPAIGNS.IMPORT.VAR_TEM_EXAMES'),
  };
  if (map[v]) return map[v];
  return v.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
};

const roleOptionsFor = col => {
  const fixed = ['ignore', 'name', 'phone', 'data', 'hora', 'exames', 'local'];
  const opts = fixed.map(value => ({
    value,
    label: roleLabel(value),
  }));
  if (!fixed.includes(col.role)) {
    opts.push({ value: col.key, label: roleLabel(col.key) });
  }
  return opts;
};

const selectedRole = col => {
  if (roleOverrides.value[col.key] !== undefined) {
    return roleOverrides.value[col.key];
  }
  return col.role;
};

const fetchParse = async () => {
  if (!rawText.value.trim()) return;
  isParsing.value = true;
  parseResult.value = null;
  waStatus.value = {};
  previewIndex.value = 0;
  try {
    const data = await store.dispatch('bulkCampaigns/parseCsv', {
      text: rawText.value,
      roles: roleOverrides.value,
    });
    parseResult.value = data;
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.IMPORT.PARSE_FAILED'));
  } finally {
    isParsing.value = false;
  }
};

const onRoleChange = async () => {
  await fetchParse();
};

const onFile = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  fileName.value = file.name;
  const reader = new FileReader();
  reader.onload = () => {
    rawText.value = String(reader.result || '');
    fetchParse();
  };
  reader.readAsText(file);
};

const checkWhatsApp = async () => {
  if (!contacts.value.length) return;
  isCheckingWa.value = true;
  try {
    const numbers = contacts.value.map((c, i) => ({
      phone: c.phone,
      validation_id: i,
    }));
    const { results } = await store.dispatch(
      'bulkCampaigns/checkWhatsApp',
      numbers
    );
    const map = {};
    (results || []).forEach(r => {
      const contact = contacts.value[r.validation_id];
      if (contact) map[contact.phone] = r.exists === true;
    });
    waStatus.value = map;
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.IMPORT.CHECK_FAILED'));
  } finally {
    isCheckingWa.value = false;
  }
};

const renderPreview = (content, contact) => {
  if (!contact) return '';
  let out = String(content || '');
  const vars = contact.vars || {};
  out = out.replace(
    /\{\{#if var\.([a-zA-Z0-9_]+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (m, key, inner) => {
      const v = vars[key];
      return v && String(v).trim() !== '' ? inner : '';
    }
  );
  out = out.replace(/\{\{var\.([a-zA-Z0-9_]+)\}\}/g, (m, key) => {
    const v = vars[key];
    return v === undefined || v === null ? '' : String(v);
  });
  const name = contact.name || contact.phone || '';
  out = out.split('{{contact.name}}').join(name);
  out = out.split('{{nome}}').join(name);
  out = out.split('{{telefone}}').join(contact.phone || '');
  let prev = null;
  let guard = 0;
  while (out !== prev && guard < 10) {
    prev = out;
    guard += 1;
    out = out.replace(/\{([^{}]*)\}/g, (m, inner) => {
      const opts = inner.split('|');
      if (opts.length < 2) return m;
      return opts[Math.floor(Math.random() * opts.length)];
    });
  }
  return out.replace(/\n{3,}/g, '\n\n');
};

const autogrow = () => {
  const el = document.getElementById('import-message');
  if (!el) return;
  el.style.height = 'auto';
  el.style.overflowY = 'hidden';
  const max = 420;
  const min = 220;
  const height = Math.max(el.scrollHeight, min);
  if (height > max) {
    el.style.height = `${max}px`;
    el.style.overflowY = 'auto';
  } else {
    el.style.height = `${height}px`;
  }
};

const insertAtCursor = (textareaId, text) => {
  const el = document.getElementById(textareaId);
  if (!el) {
    messageContent.value += text;
    return;
  }
  const start = el.selectionStart || messageContent.value.length;
  const end = el.selectionEnd || start;
  messageContent.value =
    messageContent.value.slice(0, start) +
    text +
    messageContent.value.slice(end);
  nextTick(autogrow);
};

watch(messageContent, () => nextTick(autogrow));
watch(currentStep, step => {
  if (step === 3) nextTick(autogrow);
});

const insertSnippetSet = () => {
  const opts = libraryOptions.value;
  if (!opts.length) return;
  insertAtCursor('import-message', `{${opts.join('|')}}`);
};

const unfilledSlots = content => {
  const found = new Set();
  const re = /\[\[([^[\]]+)\]\]/g;
  let m = re.exec(String(content || ''));
  while (m) {
    found.add(m[1]);
    m = re.exec(String(content || ''));
  }
  return [...found];
};

const finish = async () => {
  if (!campaignName.value.trim() || !inboxId.value) {
    alert(t('BULK_CAMPAIGNS.IMPORT.REVIEW_REQUIRED'));
    return;
  }
  const slots = unfilledSlots(messageContent.value);
  if (slots.length) {
    alert(t('BULK_CAMPAIGNS.IMPORT.LIB_UNFILLED', { slots: slots.join(', ') }));
    return;
  }
  isCreating.value = true;
  try {
    let scheduledAtIso = null;
    if (!sendNowMode.value && scheduledAt.value) {
      scheduledAtIso = new Date(scheduledAt.value).toISOString();
    }
    const payload = {
      bulk_campaign: {
        name: campaignName.value.trim(),
        content: messageContent.value.trim(),
        inbox_id: Number(inboxId.value),
        scheduled_at: scheduledAtIso,
        batch_size: 6,
        batch_interval_minutes: 3,
        delay_between_messages_min: 45,
        delay_between_messages_max: 90,
      },
      contacts_json: JSON.stringify(
        contacts.value.map(c => ({
          phone: c.phone,
          name: c.name,
          ...(c.vars || {}),
        }))
      ),
    };
    const result = await store.dispatch('bulkCampaigns/create', {
      payload,
    });
    if (sendNowMode.value && result?.id) {
      await store.dispatch('bulkCampaigns/sendNow', {
        id: result.id,
      });
    }
    alert(t('BULK_CAMPAIGNS.CREATED_SUCCESS'));
    router.push({
      name: 'bulk_campaigns_show',
      params: { id: result.id },
    });
  } catch (e) {
    alert(
      e?.response?.data?.error ||
        e?.message ||
        t('BULK_CAMPAIGNS.ERRORS.CREATE_FAILED')
    );
  } finally {
    isCreating.value = false;
  }
};

onMounted(() => {
  if (store.getters['inboxes/getInboxes'].length === 0) {
    store.dispatch('inboxes/get');
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <span class="text-heading-1 text-n-slate-12">
            {{ t('BULK_CAMPAIGNS.IMPORT.TITLE') }}
          </span>
        </div>
        <div class="flex items-center gap-1 pb-3">
          <div
            v-for="(key, i) in STEP_KEYS"
            :key="key"
            class="flex items-center gap-1 flex-1"
          >
            <button
              class="flex-1 px-2 py-1 text-xs rounded-md border transition-all"
              :class="
                i === currentStep
                  ? 'font-medium border-n-primary-300 dark:border-n-primary-700 bg-green-100 dark:bg-green-900/30 text-n-slate-12'
                  : i < currentStep
                    ? 'border-transparent text-n-text-light dark:text-n-text-muted'
                    : 'border-transparent text-n-text-light dark:text-n-text-muted opacity-50'
              "
              @click="currentStep = i"
            >
              {{ stepLabel(i, key) }}
            </button>
            <div
              v-if="i < STEP_KEYS.length - 1"
              class="w-3 h-px bg-n-divider"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl mx-auto py-4 space-y-4">
        <div v-if="currentStep === 0" class="space-y-3">
          <p class="text-sm text-n-text-light dark:text-n-text-muted">
            {{ t('BULK_CAMPAIGNS.IMPORT.PASTE_HELP') }}
          </p>
          <textarea
            v-model="rawText"
            rows="10"
            :placeholder="t('BULK_CAMPAIGNS.IMPORT.PASTE_PLACEHOLDER')"
            class="w-full px-3 py-2 font-mono text-xs border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500"
          />
          <div class="flex items-center gap-3">
            <label
              class="px-3 py-1.5 text-sm rounded-lg border border-n-divider cursor-pointer hover:opacity-80"
            >
              {{ t('BULK_CAMPAIGNS.IMPORT.CHOOSE_FILE') }}
              <input
                type="file"
                accept=".csv,.txt,.tsv"
                class="hidden"
                @change="onFile"
              />
            </label>
            <span v-if="fileName" class="text-xs text-n-text-muted">
              {{ fileName }}
            </span>
            <button
              class="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-100 dark:bg-green-900/30 border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80"
              :disabled="!rawText.trim() || isParsing"
              @click="fetchParse"
            >
              {{ t('BULK_CAMPAIGNS.IMPORT.PARSE') }}
            </button>
            <Spinner v-if="isParsing" />
          </div>
          <div v-if="parseResult && !isParsing" class="text-sm text-n-slate-12">
            {{
              t('BULK_CAMPAIGNS.IMPORT.PARSE_SUMMARY', {
                valid: stats.validos || 0,
                total: stats.linhas || 0,
                warnings: stats.avisos || 0,
              })
            }}
          </div>
          <div
            v-if="parseResult && !isParsing && flags.length"
            class="space-y-1"
          >
            <div
              v-for="(f, i) in flags"
              :key="i"
              class="text-xs text-orange-700 dark:text-orange-300"
            >
              {{ t('BULK_CAMPAIGNS.IMPORT.ROW_FLAG', { row: f.linha }) }}:
              {{ f.aviso }}
            </div>
          </div>
        </div>

        <div v-if="currentStep === 1" class="space-y-3">
          <p class="text-sm text-n-text-light dark:text-n-text-muted">
            {{ t('BULK_CAMPAIGNS.IMPORT.COLUMNS_HELP') }}
          </p>
          <div
            v-for="col in columns"
            :key="col.key"
            class="p-3 rounded-xl border border-n-divider bg-white dark:bg-n-surface-2"
          >
            <div class="flex items-center gap-3">
              <span class="font-mono text-sm text-n-slate-12">
                {{ col.key }}
              </span>
              <select
                :value="selectedRole(col)"
                class="px-2 py-1 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12"
                @change="
                  roleOverrides[col.key] = $event.target.value;
                  onRoleChange();
                "
              >
                <option
                  v-for="opt in roleOptionsFor(col)"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="mt-1 text-xs text-n-text-muted">
              {{ (col.samples || []).join(' | ') }}
            </div>
          </div>
          <div v-if="flags.length" class="space-y-1">
            <div
              v-for="(f, i) in flags"
              :key="i"
              class="text-xs text-orange-700 dark:text-orange-300"
            >
              {{ t('BULK_CAMPAIGNS.IMPORT.ROW_FLAG', { row: f.linha }) }}:
              {{ f.aviso }}
            </div>
          </div>
        </div>

        <div v-if="currentStep === 2" class="space-y-3">
          <div class="flex items-center gap-3">
            <button
              class="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-100 dark:bg-green-900/30 border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80"
              :disabled="isCheckingWa"
              @click="checkWhatsApp"
            >
              {{ t('BULK_CAMPAIGNS.IMPORT.CHECK_WA') }}
            </button>
            <Spinner v-if="isCheckingWa" />
            <span class="text-xs text-n-text-muted">
              {{
                t('BULK_CAMPAIGNS.IMPORT.WA_SUMMARY', {
                  ok: waCounts.ok,
                  missing: waCounts.missing,
                  unchecked: waCounts.unchecked,
                })
              }}
            </span>
          </div>
          <div class="rounded-xl border border-n-divider overflow-hidden">
            <div
              v-for="c in contacts"
              :key="c.phone"
              class="flex items-center gap-3 px-3 py-1.5 text-sm border-b border-n-divider last:border-0 bg-white dark:bg-n-surface-2"
            >
              <span
                class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                :class="
                  waStatus[c.phone] === true
                    ? 'bg-green-500'
                    : waStatus[c.phone] === false
                      ? 'bg-red-500'
                      : 'bg-gray-300 dark:bg-gray-600'
                "
              />
              <span class="flex-1 truncate text-n-slate-12">
                {{ c.name }}
              </span>
              <span class="font-mono text-xs text-n-text-muted">
                {{ c.phone }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="currentStep === 3" class="space-y-3">
          <div>
            <button
              class="px-3 py-1 text-sm rounded-md transition-all border text-n-slate-12 border-n-primary-300 dark:border-n-primary-700 bg-green-100 dark:bg-green-900/30"
              @click="showLibrary = !showLibrary"
            >
              {{ t('BULK_CAMPAIGNS.IMPORT.LIB_TITLE') }}
            </button>
          </div>
          <div
            v-if="showLibrary"
            class="p-3 rounded-xl border border-n-divider bg-white dark:bg-n-surface-2 space-y-2"
          >
            <div class="flex flex-wrap gap-1">
              <button
                v-for="cat in SNIPPET_CATEGORIES"
                :key="cat.key"
                class="px-2 py-0.5 text-xs rounded-md border transition-all"
                :class="
                  cat.key === libraryCategory
                    ? 'font-medium border-n-primary-300 dark:border-n-primary-700 bg-green-100 dark:bg-green-900/30 text-n-slate-12'
                    : 'text-n-text-light dark:text-n-text-muted border-transparent hover:border-n-divider'
                "
                @click="libraryCategory = cat.key"
              >
                {{
                  t(`BULK_CAMPAIGNS.IMPORT.LIB_CAT_${cat.key.toUpperCase()}`)
                }}
              </button>
            </div>
            <div
              v-for="(opt, i) in libraryOptions"
              :key="i"
              class="flex items-start gap-2 p-2 rounded-lg bg-n-surface-1 dark:bg-n-surface-3"
            >
              <span class="flex-1 text-xs text-n-slate-12">{{ opt }}</span>
              <button
                class="px-2 py-0.5 text-xs rounded-md border border-n-divider hover:opacity-80 flex-shrink-0"
                @click="insertAtCursor('import-message', opt)"
              >
                {{ t('BULK_CAMPAIGNS.IMPORT.LIB_USE_ONE') }}
              </button>
            </div>
            <button
              class="px-2 py-1 text-xs font-medium rounded-md border border-n-primary-300 dark:border-n-primary-700 bg-green-100 dark:bg-green-900/30 text-n-slate-12 hover:opacity-80"
              @click="insertSnippetSet"
            >
              {{ t('BULK_CAMPAIGNS.IMPORT.LIB_USE_SET') }}
            </button>
            <p class="text-xs text-n-text-muted">
              {{ t('BULK_CAMPAIGNS.IMPORT.LIB_HINT') }}
            </p>
          </div>
          <div class="flex flex-wrap gap-1">
            <button
              v-for="v in ['contact.name', ...varKeys]"
              :key="v"
              :title="varLabel(v)"
              class="px-2 py-0.5 text-xs font-medium rounded-md border border-n-blue-6 bg-n-blue-3 text-n-blue-11 hover:opacity-80"
              @click="insertAtCursor('import-message', varLabel(v))"
            >
              {{ varName(v) }}
            </button>
            <button
              :title="t('BULK_CAMPAIGNS.IMPORT.SPIN_TITLE')"
              class="px-2 py-0.5 text-xs rounded-md border border-n-gray-6 bg-n-gray-3 text-n-gray-11 hover:opacity-80"
              @click="
                insertAtCursor('import-message', '{opção 1|opção 2|opção 3}')
              "
            >
              {{ '{a|b|c}' }}
            </button>
          </div>
          <textarea
            id="import-message"
            v-model="messageContent"
            rows="12"
            class="w-full px-3 py-2 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500"
            @input="autogrow"
          />
          <div class="flex items-center gap-2">
            <span class="text-xs text-n-text-muted">
              {{ t('BULK_CAMPAIGNS.IMPORT.PREVIEW_AS') }}
            </span>
            <select
              v-model.number="previewIndex"
              class="px-2 py-1 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12"
            >
              <option v-for="(c, i) in contacts" :key="c.phone" :value="i">
                {{ c.name }}
              </option>
            </select>
          </div>
          <pre
            class="p-3 text-sm whitespace-pre-wrap rounded-xl border border-n-divider bg-white dark:bg-n-surface-2 text-n-slate-12"
            >{{ renderPreview(messageContent, previewContact) }}</pre
          >
        </div>

        <div v-if="currentStep === 4" class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ t('BULK_CAMPAIGNS.NAME') }}
            </label>
            <input
              v-model="campaignName"
              type="text"
              class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ t('BULK_CAMPAIGNS.INBOX') }}
            </label>
            <select
              v-model="inboxId"
              class="w-full px-3 py-2 border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500"
            >
              <option value="">
                {{ t('BULK_CAMPAIGNS.INBOX_PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>
          <div class="flex items-center gap-3">
            <label class="flex items-center gap-2 text-sm text-n-slate-12">
              <input v-model="sendNowMode" type="checkbox" />
              {{ t('BULK_CAMPAIGNS.SEND_NOW') }}
            </label>
            <input
              v-model="scheduledAt"
              type="datetime-local"
              :disabled="sendNowMode"
              class="px-2 py-1.5 text-sm border border-n-divider rounded-lg bg-white dark:bg-n-surface-2 text-n-slate-12"
            />
          </div>
          <p class="text-sm text-n-text-light dark:text-n-text-muted">
            {{
              t('BULK_CAMPAIGNS.IMPORT.REVIEW_SUMMARY', {
                count: contacts.length,
                inbox:
                  inboxes.find(i => String(i.id) === String(inboxId))?.name ||
                  '-',
                minutes: estimatedMinutes,
              })
            }}
          </p>
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg bg-green-100 dark:bg-green-900/30 border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80"
            :disabled="isCreating"
            @click="finish"
          >
            {{ t('BULK_CAMPAIGNS.IMPORT.CREATE') }}
          </button>
          <Spinner v-if="isCreating" />
        </div>

        <div class="flex items-center justify-between pt-2">
          <button
            class="px-3 py-1.5 text-sm rounded-lg border border-n-divider hover:opacity-80"
            :disabled="currentStep === 0"
            @click="currentStep -= 1"
          >
            {{ t('BULK_CAMPAIGNS.IMPORT.BACK') }}
          </button>
          <button
            v-if="currentStep < STEP_KEYS.length - 1"
            class="px-3 py-1.5 text-sm font-medium rounded-lg bg-green-100 dark:bg-green-900/30 border border-n-primary-300 dark:border-n-primary-700 text-n-slate-12 hover:opacity-80"
            :disabled="!canNext"
            @click="currentStep += 1"
          >
            {{ t('BULK_CAMPAIGNS.IMPORT.NEXT') }}
          </button>
        </div>
      </div>
    </main>
  </section>
</template>
